import { useEffect, useState } from 'react';

import { addDays } from 'date-fns';
import { baseConfig, EnvironmentName, IframeComponent, LOCALSTORAGE_PREFIX, RuntimeConfig } from 'config/config.types';
import { AxiosError } from 'axios';
import { isNotNullOrUndefined, isStringNotBlank } from 'utils/utils';
import runtimeConfigService from 'services/runtimeConfigService';
import { config as unknown } from 'config/envs/unknown';
import { config as local } from 'config/envs/local';
import { config as cloud } from 'config/envs/cloud';
import { config as sandbox } from 'config/envs/pcm-sandbox';
import { config as dev } from 'config/envs/pcm-development';
import { config as tst50 } from 'config/envs/pcm-tst50';
import { config as uat1 } from 'config/envs/pcm-uat1';
import { config as uat2 } from 'config/envs/pcm-uat2';
import { config as prd } from 'config/envs/pcm-prd';
import { config as prd2 } from 'config/envs/pcm-prd2';
import { config as prd20 } from 'config/envs/pcm-prd20';
import { JBEnvironmentId, JBEnvironmentInformation } from '@bjb-gcrm/gcrm-frontend-components';
import { FRONTEND_CONTROLLER_URL } from 'services/constants';
import { removeFromLocalStorage, setInLocalStorage } from 'utils/storageUtils';
import { isAbsoluteUrl } from 'utils/browserUtil';

export const ENV_CONFIG_DATA_LOCAL_STORAGE_KEY = LOCALSTORAGE_PREFIX + 'runtimeConfig';

const getAppVersion = () => import.meta.env.VITE_REACT_APP_VERSION;

type RuntimeConfigLocalstorageData = {
  configVersion: string | undefined;
  validUntil: number;
  configState: RuntimeConfig;
};

const environmentConfigs: Record<EnvironmentName, RuntimeConfig> = {
  local,
  cloud,
  'pcm-sandbox': sandbox,
  'pcm-skaffold': sandbox,
  'pcm-development': dev,
  'pcm-tst50': tst50,
  'pcm-uat1': uat1,
  'pcm-uat2': uat2,
  'pcm-prd': prd,
  'pcm-prd2': prd2,
  'pcm-prd20': prd20,
  unknown,
};

const environmentToJbEnv: Record<EnvironmentName, JBEnvironmentId> = {
  local: 'LOCAL',
  cloud: 'CLOUD',
  'pcm-sandbox': 'SANDBOX',
  'pcm-skaffold': 'SANDBOX',
  'pcm-development': 'DEV',
  'pcm-tst50': 'TST50',
  'pcm-uat1': 'UAT1',
  'pcm-uat2': 'UAT2',
  'pcm-prd': 'PRD',
  'pcm-prd2': 'PRD2',
  'pcm-prd20': 'PRD20',
  unknown: 'UNKNOWN',
};

export type RuntimeConfigType = {
  configData: RuntimeConfig;
  error?: AxiosError;
  originUrls: string[];
  isLoading: boolean;
  envInfo: JBEnvironmentInformation;
};

const getOriginUrls = (configData: RuntimeConfig) => {
  const originUrls = configData.urls?.filter((url) => isStringNotBlank(url)).map((url) => new URL(url).origin);
  return [...new Set(originUrls)];
};

const fixLocalIframeUrls = (iframeUrls: Record<IframeComponent, string>) => {
  const copy: Record<IframeComponent, string> = { ...iframeUrls };
  for (const [key, value] of Object.entries(copy)) {
    copy[key as IframeComponent] = isAbsoluteUrl(value) ? value : FRONTEND_CONTROLLER_URL + value;
  }
  return copy;
};

export const useRuntimeConfig = (): RuntimeConfigType => {
  const [configData, setConfigData] = useState<RuntimeConfig>(getInitialRuntimeConfig());
  const [error, setError] = useState<AxiosError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const storedConfigData = getConfigDataFromLocalStorage();
    if (isStorageDataValid(storedConfigData)) {
      setConfigData(storedConfigData.configState);
      setIsLoading(false);
    } else {
      runtimeConfigService
        .getRuntimeConfig()
        .then((configState) => {
          const effectiveRuntimeConfig = getEffectiveConfig(configState);
          putConfigDataToLocalStorage(effectiveRuntimeConfig);
          setConfigData(effectiveRuntimeConfig);
          setIsLoading(false);
        })
        .catch((error) => {
          clearConfigDataFromLocalStorage();
          setConfigData(unknown);
          setError(error);
          setIsLoading(false);
        });
    }
  }, []);

  return {
    configData: {
      ...configData,
      iframeUrls: fixLocalIframeUrls(configData.iframeUrls),
    },
    originUrls: getOriginUrls(configData),
    error,
    isLoading,
    envInfo: {
      componentId: 'clm-portal-frontend',
      appId: 'PCM',
      envId: environmentToJbEnv[configData.env] ?? 'UNKNOWN',
    },
  };
};

const getEffectiveConfig = (fetchedRuntimeConfig: RuntimeConfig): RuntimeConfig => {
  const runtimeConfig = environmentConfigs[fetchedRuntimeConfig.env] ?? baseConfig;
  return { ...runtimeConfig, ...fetchedRuntimeConfig };
};

const getInitialRuntimeConfig = () => {
  const storedConfigData = getConfigDataFromLocalStorage();

  if (isStorageDataValid(storedConfigData)) {
    return storedConfigData.configState;
  }
  return unknown;
};

export const putConfigDataToLocalStorage = (
  configState: RuntimeConfig,
  validUntil?: number,
  configVersion: string | undefined = getAppVersion(),
) => {
  const definitiveValidUntil = isNotNullOrUndefined(validUntil) ? validUntil : addDays(new Date(), 1).getTime();
  const value: RuntimeConfigLocalstorageData = {
    configState,
    validUntil: definitiveValidUntil,
    configVersion,
  };
  setInLocalStorage(ENV_CONFIG_DATA_LOCAL_STORAGE_KEY, value);
};

export const getConfigDataFromLocalStorage = (): RuntimeConfigLocalstorageData | undefined => {
  const valueFromLocalStorage = localStorage.getItem(ENV_CONFIG_DATA_LOCAL_STORAGE_KEY);
  if (isStringNotBlank(valueFromLocalStorage)) {
    try {
      return JSON.parse(valueFromLocalStorage);
    } catch {
      // eslint-disable-next-line no-console
      console.error(`Parsing of ${ENV_CONFIG_DATA_LOCAL_STORAGE_KEY} from local storage failed`);
    }
  }
  return undefined;
};

export const clearConfigDataFromLocalStorage = () => removeFromLocalStorage(ENV_CONFIG_DATA_LOCAL_STORAGE_KEY);

const isNowBefore = (validUntil?: number) => {
  const currentTime: number = Date.now();
  return isNotNullOrUndefined(validUntil) && validUntil > currentTime;
};

const isStorageDataValid = (
  storedConfigData: RuntimeConfigLocalstorageData | undefined,
): storedConfigData is RuntimeConfigLocalstorageData =>
  isNotNullOrUndefined(storedConfigData) &&
  isNotNullOrUndefined(storedConfigData.configState) &&
  isNotNullOrUndefined(storedConfigData.configState.env) &&
  storedConfigData.configState.env !== 'unknown' &&
  isNotNullOrUndefined(environmentConfigs[storedConfigData.configState.env]) &&
  isNowBefore(storedConfigData.validUntil) &&
  storedConfigData?.configVersion === getAppVersion();
